/**
 * useDocSearch — Documentation search hook.
 *
 * If the widget config includes a Charlotte RAG endpoint, searches remotely.
 * Otherwise, performs client-side text search over config.docs[].
 */
import { useState, useRef, useCallback, useMemo } from 'react';
import type { DocSearchResult, DocSection, CharlotteConfig } from '../types';

interface DocSearchOptions {
  /** Charlotte RAG config (uses endpoint for remote search) */
  charlotte?: CharlotteConfig;
  /** Static doc sections for client-side fallback */
  docs?: DocSection[];
  /** RAG collection name override */
  ragCollection?: string;
}

interface DocSearchReturn {
  results: DocSearchResult[];
  loading: boolean;
  search: (query: string) => void;
  clear: () => void;
}

interface SearchCache {
  [query: string]: DocSearchResult[];
}

/** Simple snippet extraction: find the best matching window around the query terms. */
function extractSnippet(content: string, query: string, maxLen = 160): string {
  const lower = content.toLowerCase();
  const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
  let bestIdx = 0;
  let bestScore = -1;

  for (let i = 0; i < lower.length - 40; i += 20) {
    const window = lower.slice(i, i + maxLen);
    let score = 0;
    for (const term of terms) {
      if (window.includes(term)) score++;
    }
    if (score > bestScore) {
      bestScore = score;
      bestIdx = i;
    }
  }

  const start = Math.max(0, bestIdx);
  const slice = content.slice(start, start + maxLen).trim();
  return (start > 0 ? '...' : '') + slice + (start + maxLen < content.length ? '...' : '');
}

/** Flatten nested doc sections into a flat list */
function flattenDocs(sections: DocSection[]): DocSection[] {
  const result: DocSection[] = [];
  function walk(items: DocSection[]) {
    for (const item of items) {
      result.push(item);
      if (item.children) walk(item.children);
    }
  }
  walk(sections);
  return result;
}

/** Client-side text search scoring */
function scoreDoc(doc: DocSection, query: string): number {
  const q = query.toLowerCase();
  const terms = q.split(/\s+/).filter(Boolean);
  let score = 0;

  const titleLower = doc.title.toLowerCase();
  const contentLower = doc.content.toLowerCase();
  const keywordsLower = (doc.keywords ?? []).join(' ').toLowerCase();

  for (const term of terms) {
    if (titleLower.includes(term)) score += 3;
    if (keywordsLower.includes(term)) score += 2;
    if (contentLower.includes(term)) score += 1;
  }

  return score;
}

export function useDocSearch(options: DocSearchOptions): DocSearchReturn {
  const { charlotte, docs, ragCollection } = options;
  const [results, setResults] = useState<DocSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  const cacheRef = useRef<SearchCache>({});
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const flatDocs = useMemo(() => flattenDocs(docs ?? []), [docs]);

  const doRemoteSearch = useCallback(
    async (query: string) => {
      if (!charlotte?.endpoint) return;

      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      setLoading(true);
      try {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' };
        if (charlotte.auth) {
          const token =
            typeof charlotte.auth === 'function' ? await charlotte.auth() : charlotte.auth;
          headers['Authorization'] = `Bearer ${token}`;
        }

        const res = await fetch(charlotte.endpoint, {
          method: 'POST',
          headers,
          body: JSON.stringify({
            query,
            collection: ragCollection,
            top_k: 5,
          }),
          signal: controller.signal,
        });

        if (!res.ok) throw new Error(`Search failed: ${res.status}`);

        const data = (await res.json()) as {
          results?: Array<{
            id?: string;
            title?: string;
            content?: string;
            snippet?: string;
            score?: number;
          }>;
        };

        const mapped: DocSearchResult[] = (data.results ?? []).map((r, i) => ({
          sectionId: r.id ?? `rag-${i}`,
          title: r.title ?? 'Untitled',
          snippet: r.snippet ?? extractSnippet(r.content ?? '', query),
          score: r.score ?? 0,
        }));

        cacheRef.current[query] = mapped;
        if (!controller.signal.aborted) {
          setResults(mapped);
        }
      } catch (err) {
        if (err instanceof DOMException && err.name === 'AbortError') return;
        // Fall back to client-side on remote failure
        doLocalSearch(query);
      } finally {
        if (!controller.signal.aborted) setLoading(false);
      }
    },
    [charlotte, ragCollection], // eslint-disable-line react-hooks/exhaustive-deps
  );

  const doLocalSearch = useCallback(
    (query: string) => {
      if (!query.trim()) {
        setResults([]);
        return;
      }

      const scored = flatDocs
        .map((doc) => ({ doc, score: scoreDoc(doc, query) }))
        .filter((r) => r.score > 0)
        .sort((a, b) => b.score - a.score)
        .slice(0, 10);

      const mapped: DocSearchResult[] = scored.map((r) => ({
        sectionId: r.doc.id,
        title: r.doc.title,
        snippet: extractSnippet(r.doc.content, query),
        score: r.score / (query.split(/\s+/).length * 3), // normalize to 0-1 range
      }));

      cacheRef.current[query] = mapped;
      setResults(mapped);
      setLoading(false);
    },
    [flatDocs],
  );

  const search = useCallback(
    (query: string) => {
      if (timerRef.current) clearTimeout(timerRef.current);

      if (!query.trim()) {
        setResults([]);
        setLoading(false);
        return;
      }

      // Check cache
      if (cacheRef.current[query]) {
        setResults(cacheRef.current[query]);
        return;
      }

      setLoading(true);

      // 300ms debounce
      timerRef.current = setTimeout(() => {
        if (charlotte?.endpoint) {
          doRemoteSearch(query);
        } else {
          doLocalSearch(query);
        }
      }, 300);
    },
    [charlotte?.endpoint, doRemoteSearch, doLocalSearch],
  );

  const clear = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current);
    abortRef.current?.abort();
    setResults([]);
    setLoading(false);
  }, []);

  return { results, loading, search, clear };
}
